/**
 * Authentication adapter using Logto.
 *
 * Provides React context and hooks for authentication.
 * Part of powercore pilot migration.
 */

import { LogtoProvider, useLogto, useHandleSignInCallback, type LogtoConfig } from "@logto/react"
import { type ReactNode, createContext, useContext, useEffect, useState, useRef, useMemo, useCallback } from "react"
import { setTokenGetter } from "./api"
import { setAuthErrorHandler } from "../lib/api"

// Logto configuration from environment
const logtoConfig: LogtoConfig = {
  endpoint: import.meta.env.VITE_LOGTO_ENDPOINT || "https://o0pdsf.logto.app",
  appId: import.meta.env.VITE_LOGTO_APP_ID || "b6m4deziaa7tkcloiy51s",
  // Resources for API access (optional)
  resources: import.meta.env.VITE_LOGTO_API_RESOURCE
    ? [import.meta.env.VITE_LOGTO_API_RESOURCE]
    : undefined,
}

// Callback URL from environment
const CALLBACK_URL = import.meta.env.VITE_LOGTO_CALLBACK_URL || `${window.location.origin}/callback`
const POST_LOGOUT_URL = import.meta.env.VITE_LOGTO_POST_LOGOUT_URL || window.location.origin

// User type matching backend UserIdentity
export interface User {
  id: string
  email?: string
  name?: string
  roles?: string[]
}

// Auth context type
interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  user: User | null
  accessToken: string | null
  signIn: () => Promise<void>
  signOut: () => Promise<void>
  getAccessToken: () => Promise<string | null>
}

const AuthContext = createContext<AuthContextType | null>(null)

/**
 * Internal auth provider that uses Logto hooks.
 */
function AuthProviderInner({ children }: { children: ReactNode }) {
  const {
    isAuthenticated,
    isLoading,
    getIdTokenClaims,
    getAccessToken: logtoGetAccessToken,
    signIn: logtoSignIn,
    signOut: logtoSignOut,
  } = useLogto()


  const [user, setUser] = useState<User | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)

  // Ref to hold the latest logtoGetAccessToken function
  const logtoGetAccessTokenRef = useRef(logtoGetAccessToken)
  useEffect(() => {
    logtoGetAccessTokenRef.current = logtoGetAccessToken
  }, [logtoGetAccessToken])

  // Refs to hold latest Logto functions (these change on every render)
  const getIdTokenClaimsRef = useRef(getIdTokenClaims)
  const logtoSignInRef = useRef(logtoSignIn)
  const logtoSignOutRef = useRef(logtoSignOut)

  useEffect(() => {
    getIdTokenClaimsRef.current = getIdTokenClaims
    logtoSignInRef.current = logtoSignIn
    logtoSignOutRef.current = logtoSignOut
  }, [getIdTokenClaims, logtoSignIn, logtoSignOut])

  // Refs to track current values to avoid unnecessary state updates
  const userRef = useRef<User | null>(null)
  const accessTokenRef = useRef<string | null>(null)

  // Fetch user info when authenticated (runs once when isAuthenticated becomes true)
  useEffect(() => {
    if (isAuthenticated) {
      getIdTokenClaimsRef.current().then((claims) => {
        if (claims) {
          const newUser: User = {
            id: claims.sub,
            email: claims.email as string | undefined,
            name: claims.name as string | undefined,
            roles: (claims.roles as string[]) || [],
          }
          // Only update if user actually changed
          if (!userRef.current || userRef.current.id !== newUser.id) {
            userRef.current = newUser
            setUser(newUser)
          }
        }
      })

      // Get access token for API calls (with resource as audience)
      // The resource must be configured in VITE_LOGTO_API_RESOURCE
      logtoGetAccessTokenRef.current(import.meta.env.VITE_LOGTO_API_RESOURCE).then((token) => {
        const newToken = token ?? null
        if (newToken !== accessTokenRef.current) {
          accessTokenRef.current = newToken
          setAccessToken(newToken)
        }
      }).catch(() => {
        if (accessTokenRef.current !== null) {
          accessTokenRef.current = null
          setAccessToken(null)
        }
      })
    } else {
      if (userRef.current !== null) {
        userRef.current = null
        setUser(null)
      }
      if (accessTokenRef.current !== null) {
        accessTokenRef.current = null
        setAccessToken(null)
      }
    }
  }, [isAuthenticated]) // Only depend on isAuthenticated, use refs for functions

  // Memoize signIn to maintain stable reference (uses ref for Logto function)
  const signIn = useCallback(async () => {
    await logtoSignInRef.current(CALLBACK_URL)
  }, [])

  // Memoize signOut to maintain stable reference (uses ref for Logto function)
  const signOut = useCallback(async () => {
    await logtoSignOutRef.current(POST_LOGOUT_URL)
  }, [])

  // Stable token getter that uses the ref to always get latest logtoGetAccessToken
  // IMPORTANT: This function should NEVER update React state to avoid infinite re-render loops
  // The accessToken state is only used for display purposes and is updated in the auth effect above
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      // Use access token for API calls (it's a verifiable JWT with resource as audience)
      const token = await logtoGetAccessTokenRef.current(import.meta.env.VITE_LOGTO_API_RESOURCE)
      return token ?? null
    } catch {
      return null
    }
  }, [])

  // Register token getter for API client (only once, uses ref internally)
  useEffect(() => {
    setTokenGetter(getAccessToken)
  }, [getAccessToken])

  // Register auth error handler for automatic logout on 401
  useEffect(() => {
    setAuthErrorHandler(() => {
      // Sign out and redirect to login
      signOut()
    })
  }, [signOut])

  // Memoize context value to prevent unnecessary re-renders of consumers
  const contextValue = useMemo(() => ({
    isAuthenticated,
    isLoading,
    user,
    accessToken,
    signIn,
    signOut,
    getAccessToken,
  }), [isAuthenticated, isLoading, user, accessToken, signIn, signOut, getAccessToken])

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

/**
 * Auth provider component.
 * Wrap your app with this to enable authentication.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  return (
    <LogtoProvider config={logtoConfig}>
      <AuthProviderInner>{children}</AuthProviderInner>
    </LogtoProvider>
  )
}

/**
 * Hook to access auth context.
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

/**
 * Callback component for handling OAuth redirect.
 * Mount this at /callback route.
 */
export function AuthCallback({ onSuccess, onError }: {
  onSuccess?: () => void
  onError?: (error: Error) => void
} = {}) {
  const { isLoading, error } = useHandleSignInCallback(() => {
    onSuccess?.()
    // Redirect to home after successful login
    window.location.href = "/"
  })

  useEffect(() => {
    if (error) {
      onError?.(error as Error)
    }
  }, [error, onError])

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-xl font-bold text-red-600">Authentication Error</h1>
          <p className="text-gray-600 mt-2">{String(error)}</p>
          <a href="/" className="text-mazo-600 mt-4 inline-block">Go back home</a>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="text-gray-600 mt-4">Completing sign in...</p>
        </div>
      </div>
    )
  }

  return null
}
